/**
 * Contratos Zod compartilhados entre API, workers e frontend.
 *
 * A mesma definição valida o corpo da requisição HTTP, a saída do LLM e o payload que
 * chega pelo WebSocket. Um só lugar para mudar quando o formato evolui.
 */
import { z } from 'zod';
export const frameRateSchema = z.object({
    num: z.number().int().positive(),
    den: z.number().int().positive(),
});
export const transformSchema = z.object({
    x: z.number(),
    y: z.number(),
    scale: z.number().positive(),
    scaleX: z.number().positive().optional(),
    scaleY: z.number().positive().optional(),
    rotation: z.number(),
    opacity: z.number().min(0).max(1),
    anchorX: z.number(),
    anchorY: z.number(),
});
export const cropSchema = z.object({
    top: z.number().min(0).max(1),
    right: z.number().min(0).max(1),
    bottom: z.number().min(0).max(1),
    left: z.number().min(0).max(1),
});
export const keyframePropertySchema = z.enum([
    'transform.x',
    'transform.y',
    'transform.scale',
    'transform.rotation',
    'transform.opacity',
    'crop.top',
    'crop.right',
    'crop.bottom',
    'crop.left',
    'volume',
]);
export const keyframeSchema = z.object({
    id: z.string(),
    property: keyframePropertySchema,
    time: z.number(),
    value: z.number(),
    easing: z.enum(['linear', 'ease-in', 'ease-out', 'ease-in-out', 'hold']),
});
export const effectSchema = z.object({
    id: z.string(),
    type: z.string(),
    enabled: z.boolean(),
    params: z.record(z.union([z.number(), z.string(), z.boolean()])),
});
export const captionWordSchema = z.object({
    text: z.string(),
    start: z.number(),
    end: z.number(),
    highlight: z.boolean().optional(),
    speaker: z.string().optional(),
});
export const itemSourceSchema = z.object({
    assetId: z.string(),
    in: z.number().min(0),
    out: z.number().min(0),
});
export const timelineItemSchema = z.object({
    id: z.string(),
    kind: z.enum(['video', 'audio', 'image', 'text', 'caption', 'shape']),
    start: z.number().min(0),
    duration: z.number().positive(),
    source: itemSourceSchema.optional(),
    speed: z.number().positive(),
    reverse: z.boolean(),
    preservePitch: z.boolean(),
    transform: transformSchema,
    crop: cropSchema,
    volume: z.number().min(0).max(2),
    muted: z.boolean(),
    fadeIn: z.number().min(0),
    fadeOut: z.number().min(0),
    effects: z.array(effectSchema),
    keyframes: z.array(keyframeSchema),
    text: z.record(z.unknown()).optional(),
    caption: z.record(z.unknown()).optional(),
    label: z.string().optional(),
    locked: z.boolean(),
    groupId: z.string().optional(),
    meta: z.record(z.unknown()),
});
export const trackSchema = z.object({
    id: z.string(),
    kind: z.enum(['video', 'audio', 'image', 'text', 'caption', 'music', 'sfx', 'adjustment', 'overlay']),
    name: z.string(),
    index: z.number().int().min(0),
    locked: z.boolean(),
    hidden: z.boolean(),
    muted: z.boolean(),
    solo: z.boolean(),
    height: z.number().positive(),
    volume: z.number().min(0).max(2),
    items: z.array(timelineItemSchema),
});
export const markerSchema = z.object({
    id: z.string(),
    time: z.number().min(0),
    name: z.string(),
    color: z.string(),
    note: z.string().optional(),
});
export const sequenceSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    frameRate: frameRateSchema,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sampleRate: z.number().int().positive(),
    backgroundColor: z.string(),
    tracks: z.array(trackSchema),
    markers: z.array(markerSchema),
    revision: z.number().int().min(0),
});
export const commandTypeSchema = z.enum([
    'ADD_TRACK', 'REMOVE_TRACK', 'UPDATE_TRACK', 'REORDER_TRACKS',
    'ADD_ITEM', 'REMOVE_ITEMS', 'UPDATE_ITEMS', 'MOVE_ITEMS',
    'TRIM_ITEM', 'SPLIT_ITEM', 'DUPLICATE_ITEMS',
    'DELETE_RANGE', 'LIFT_RANGE', 'INSERT_GAP',
    'SET_SPEED', 'SET_TRANSFORM', 'SET_CROP', 'SET_VOLUME',
    'SET_KEYFRAMES', 'ADD_KEYFRAME', 'REMOVE_KEYFRAMES', 'UPDATE_KEYFRAME',
    'ADD_EFFECT', 'REMOVE_EFFECT', 'UPDATE_EFFECT',
    'ADD_TEXT', 'UPDATE_TEXT', 'ADD_CAPTIONS', 'UPDATE_CAPTION_STYLE', 'SET_CAPTION_WORDS',
    'ADD_MARKER', 'REMOVE_MARKER', 'UPDATE_MARKER',
    'GROUP_ITEMS', 'UNGROUP_ITEMS', 'SET_SEQUENCE_SETTINGS',
    'RESTORE_TRACKS', 'BATCH',
]);
export const commandSchema = z.lazy(() => z.object({
    id: z.string().min(1),
    type: commandTypeSchema,
    sequenceId: z.string().min(1),
    payload: z.record(z.unknown()),
    source: z.enum(['USER', 'AI', 'SYSTEM']),
    actorId: z.string().optional(),
    changeId: z.string().optional(),
    reason: z.string().max(500).optional(),
    createdAt: z.string(),
}));
export const applyCommandsBody = z.object({
    commands: z.array(commandSchema).min(1).max(500),
    /** Revisão sobre a qual o cliente aplicou localmente. Detecta conflito. */
    baseRevision: z.number().int().min(0).optional(),
});
// ---------------------------------------------------------------------------
// Auth e organização
// ---------------------------------------------------------------------------
export const registerBody = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(128),
    name: z.string().min(1).max(120),
});
export const loginBody = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const workspaceRoleSchema = z.enum(['OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'CLIENT']);
export const createWorkspaceBody = z.object({
    name: z.string().min(1).max(120),
});
export const createProjectBody = z.object({
    workspaceId: z.string(),
    name: z.string().min(1).max(160),
    width: z.number().int().min(16).max(7680).optional(),
    height: z.number().int().min(16).max(4320).optional(),
    frameRate: frameRateSchema.optional(),
});
// ---------------------------------------------------------------------------
// Mídia
// ---------------------------------------------------------------------------
export const VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];
export const AUDIO_MIME = ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/aac', 'audio/mp4'];
export const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];
export const ACCEPTED_MIME = [...VIDEO_MIME, ...AUDIO_MIME, ...IMAGE_MIME];
/** 8 GB. Acima disso o upload é recusado antes de gastar banda. */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024 * 1024;
export const initUploadBody = z.object({
    projectId: z.string(),
    filename: z.string().min(1).max(300),
    contentType: z.string().refine((v) => ACCEPTED_MIME.includes(v), {
        message: 'Formato não suportado',
    }),
    size: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
export const completeUploadBody = z.object({
    parts: z
        .array(z.object({ partNumber: z.number().int().positive(), etag: z.string() }))
        .optional(),
});
// ---------------------------------------------------------------------------
// IA
// ---------------------------------------------------------------------------
export const silenceRemovalBody = z.object({
    /** Silêncio mínimo para considerar remoção (s). */
    minSilence: z.number().min(0.05).max(10).default(0.5),
    /** Quanto de silêncio preservar em cada lado do corte (s). */
    padding: z.number().min(0).max(2).default(0.25),
    /** dBFS abaixo do qual o trecho conta como silêncio. */
    threshold: z.number().min(-90).max(-10).default(-35),
    trackIds: z.array(z.string()).optional(),
    /** Só analisa e devolve o ChangeSet, sem aplicar. */
    preview: z.boolean().default(true),
});
export const fillerLevelSchema = z.enum(['CONSERVADOR', 'NORMAL', 'AGRESSIVO']);
export const fillerRemovalBody = z.object({
    level: fillerLevelSchema.default('NORMAL'),
    removeFillers: z.boolean().default(true),
    removeRepetitions: z.boolean().default(true),
    removeFalseStarts: z.boolean().default(false),
    preview: z.boolean().default(true),
});
export const aiPromptBody = z.object({
    prompt: z.string().min(1).max(2000),
    /** Aplica direto em vez de gerar um ChangeSet revisável. */
    autoApply: z.boolean().default(false),
});
export const autoEditGoalSchema = z.enum([
    'REELS', 'TIKTOK', 'SHORTS', 'YOUTUBE', 'PODCAST', 'ANUNCIO', 'INSTITUCIONAL', 'UGC',
]);
export const autoEditPaceSchema = z.enum(['NATURAL', 'DINAMICO', 'MUITO_DINAMICO']);
export const aspectRatioSchema = z.enum(['16:9', '9:16', '4:5', '1:1']);
export const autoEditBody = z.object({
    goal: autoEditGoalSchema,
    /** null = duração automática. */
    targetDuration: z.number().min(5).max(3600).nullable().default(null),
    aspectRatio: aspectRatioSchema.default('9:16'),
    pace: autoEditPaceSchema.default('DINAMICO'),
    features: z
        .object({
        removeSilence: z.boolean().default(true),
        removeFillers: z.boolean().default(true),
        removeRepetitions: z.boolean().default(true),
        captions: z.boolean().default(true),
        smartZoom: z.boolean().default(true),
        autoReframe: z.boolean().default(true),
        enhanceVoice: z.boolean().default(true),
        brandKit: z.boolean().default(true),
    })
        .default({}),
    brandKitId: z.string().optional(),
});
export const captionStyleSchema = z.enum(['sentence', 'word', 'karaoke', 'tiktok']);
export const generateCaptionsBody = z.object({
    style: captionStyleSchema.default('karaoke'),
    maxWordsPerLine: z.number().int().min(1).max(12).default(5),
    highlightKeywords: z.boolean().default(true),
    presetId: z.string().optional(),
    trackId: z.string().optional(),
});
export const smartZoomBody = z.object({
    intensity: z.enum(['BAIXA', 'MEDIA', 'ALTA']).default('MEDIA'),
    frequency: z.enum(['BAIXA', 'MEDIA', 'ALTA']).default('MEDIA'),
    itemIds: z.array(z.string()).optional(),
});
export const autoReframeBody = z.object({
    aspectRatio: aspectRatioSchema,
    itemIds: z.array(z.string()).optional(),
    /** Segue rosto quando detectado; sem detecção, cai para o centro ponderado. */
    followSubject: z.boolean().default(true),
});
export const enhanceVoiceBody = z.object({
    denoise: z.boolean().default(true),
    dereverb: z.boolean().default(true),
    deesser: z.boolean().default(true),
    normalize: z.boolean().default(true),
    /** LUFS alvo. -14 é o padrão de plataformas sociais. */
    targetLufs: z.number().min(-30).max(-6).default(-14),
    itemIds: z.array(z.string()).optional(),
});
// ---------------------------------------------------------------------------
// Exportação
// ---------------------------------------------------------------------------
export const exportPresetSchema = z.enum([
    'INSTAGRAM_REELS', 'TIKTOK', 'YOUTUBE', 'YOUTUBE_SHORTS', 'INSTAGRAM_FEED', 'CUSTOM',
]);
export const createExportBody = z.object({
    preset: exportPresetSchema.default('YOUTUBE'),
    width: z.number().int().min(16).max(7680).optional(),
    height: z.number().int().min(16).max(4320).optional(),
    frameRate: frameRateSchema.optional(),
    videoBitrate: z.number().int().min(200_000).max(200_000_000).optional(),
    audioBitrate: z.number().int().min(32_000).max(512_000).optional(),
    codec: z.enum(['h264', 'h265']).optional(),
    quality: z.enum(['DRAFT', 'STANDARD', 'HIGH']).default('STANDARD'),
});
//# sourceMappingURL=schemas.js.map